import {Color} from '@benchling/nucleus-ui';
import {createStyles, Divider, Group, Switch, Text} from '@mantine/core';
import React from 'react';

import {
  ProcedureRunDataEntryMode,
  ProcedureType,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcedureChip from 'ui-library/chips/ProcedureChip';

import {
  EditNumConditionGroupsButtons,
  planConditionGroupsButtonVariant,
  runConditionGroupsButtonVariant,
} from '../conditions/EditNumConditionGroupsButtons';
import {ConditionGroup} from '../design-state/useExperimentDesignState';
import {MethodExecutionLimitState} from '../execution-status/useMethodExecutionLimitState';
import ConditionsSection from './ConditionsSection';
import PlanSettingsSection from './PlanSettingsSection';
import {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';

interface BaseProcedureRunDesignEditorLeftSidepanelProps<TCondition> {
  isPlan: boolean;
  conditions: Array<TCondition>;
  setConditions: (conditions: Array<TCondition>, prevIndexes: Array<number | null>) => void;
  experimentalInputs: ExperimentalInputs;
  setExperimentalInputs: (experimentalInputs: ExperimentalInputs) => void;
  editState: StudyDesignTabEditState;
  procedureType: ProcedureType;
  methodExecutionLimitState: MethodExecutionLimitState;
  studyType: StudyType;
  dataEntryMode: ProcedureRunDataEntryMode;
  procedureId: string;
  flowchartVersionId: string;
  newConditionCoveredMethodIds?: ReadonlyArray<string>;
}

interface ProcedureRunDesignEditorLeftSidepanelPlanProps extends BaseProcedureRunDesignEditorLeftSidepanelProps<ConditionGroup> {
  isPlan: true;
  onRemoveProcedure: () => Promise<void>;
  setDataEntryMode: (dataEntryMode: ProcedureRunDataEntryMode) => void;
  hasUpdateAvailable: boolean;
  isProcedureVersionUpdateDisabled?: boolean;
  onOpenUpdateModal: () => void;
}

interface ProcedureRunDesignEditorLeftSidepanelRunProps extends BaseProcedureRunDesignEditorLeftSidepanelProps<ConditionGroup> {
  isPlan: false;
  isSplitPoolEnabled: boolean;
  activeMethodName: string | null;
  doesActiveMethodHaveDownstreamMethods: boolean;
  showArchived: boolean;
  onShowArchivedChange: (showArchived: boolean) => void;
  pendingRemovedByConditionIndex: ReadonlyMap<number, number>;
}

type ProcedureRunDesignEditorLeftSidepanelProps =
  | ProcedureRunDesignEditorLeftSidepanelPlanProps
  | ProcedureRunDesignEditorLeftSidepanelRunProps;

function AddRemoveConditionsInfoBanner({
  activeMethodName,
  doesActiveMethodHaveDownstreamMethods,
}: {
  activeMethodName?: string | null;
  doesActiveMethodHaveDownstreamMethods?: boolean;
}): JSX.Element | null {
  const {classes} = useStyles();
  // No active method
  if (activeMethodName == null) {
    return null;
  }
  const bannerText = (
    <span className={classes.splitPoolBannerText}>
      Adding or removing condition replicates applies to{' '}
      <span className={classes.splitPoolActiveMethodName}>{activeMethodName}</span>
      {doesActiveMethodHaveDownstreamMethods ? ' and all downstream operations.' : '.'}
    </span>
  );
  return (
    <div className={classes.infoBannerWrapper}>
      <InlineBanner bannerType={InlineBannerType.INFO} supportingInfo={bannerText} />
    </div>
  );
}

export default function ProcedureRunDesignEditorLeftSidepanel(
  props: ProcedureRunDesignEditorLeftSidepanelProps
): JSX.Element {
  const {classes} = useStyles();
  const isLocked = props.editState !== StudyDesignTabEditState.EDITING;
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, props.procedureType, {
    stringCase: StringCase.LOWER,
  });

  const editNumColsButtons = (
    <EditNumConditionGroupsButtons
      conditions={props.conditions}
      setConditions={props.setConditions}
      methodExecutionLimitState={props.methodExecutionLimitState}
      procedureType={props.procedureType}
      showReplicateLimitLabel={false}
      newConditionCoveredMethodIds={props.newConditionCoveredMethodIds}
      {...(props.isPlan
        ? planConditionGroupsButtonVariant({
            conditions: props.conditions,
            methodExecutionLimitState: props.methodExecutionLimitState,
            procedureType: props.procedureType,
          })
        : runConditionGroupsButtonVariant({
            conditions: props.conditions,
            methodExecutionLimitState: props.methodExecutionLimitState,
            procedureType: props.procedureType,
          }))}
    />
  );
  const conditionsSection = (
    <ConditionsSection
      isPlan={props.isPlan}
      conditions={props.conditions}
      setConditions={props.setConditions}
      experimentalInputs={props.experimentalInputs}
      setExperimentalInputs={props.setExperimentalInputs}
      editNumColsButtons={editNumColsButtons}
      locked={isLocked}
      methodExecutionLimitState={props.methodExecutionLimitState}
      procedureType={props.procedureType}
      newConditionCoveredMethodIds={props.newConditionCoveredMethodIds}
      archivedDisplay={
        !props.isPlan && props.isSplitPoolEnabled
          ? {
              showArchived: props.showArchived,
              pendingRemovedByConditionIndex: props.pendingRemovedByConditionIndex,
            }
          : undefined
      }
      dataTestAttrs={{component: 'TransposedReplicateTable', element: 'replicateTable'}}
    />
  );

  if (props.isPlan) {
    return (
      <div className={classes.sidepanelContent}>
        {conditionsSection}
        <Divider
          className={classes.planSettingsDivider}
          color={Color.GRAY_200}
          {...getTestProps({
            component: 'ProcedureRunDesignEditorLeftSidepanel',
            element: 'planSettingsDivider',
          })}
        />
        <PlanSettingsSection
          editState={props.editState}
          procedureId={props.procedureId}
          flowchartVersionId={props.flowchartVersionId}
          procedureType={props.procedureType}
          studyType={props.studyType}
          onRemoveProcedure={props.onRemoveProcedure}
          hasUpdateAvailable={props.hasUpdateAvailable}
          isProcedureVersionUpdateDisabled={props.isProcedureVersionUpdateDisabled}
          onOpenUpdateModal={props.onOpenUpdateModal}
          dataEntryMode={props.dataEntryMode}
          setDataEntryMode={props.setDataEntryMode}
        />
      </div>
    );
  }
  return (
    <div className={classes.sidepanelContent}>
      {props.editState === StudyDesignTabEditState.EDITING && props.isSplitPoolEnabled && (
        <AddRemoveConditionsInfoBanner
          activeMethodName={props.activeMethodName}
          doesActiveMethodHaveDownstreamMethods={props.doesActiveMethodHaveDownstreamMethods}
        />
      )}
      {conditionsSection}
      {props.isSplitPoolEnabled && (
        <div className={classes.showArchivedWrapper}>
          <Switch
            label="Show archived"
            classNames={{label: classes.showArchivedLabel}}
            checked={props.showArchived}
            onChange={(event) => {
              props.onShowArchivedChange(event.currentTarget.checked);
            }}
          />
        </div>
      )}
      <div className={classes.runSectionWrapper}>
        <Group spacing="xs" noWrap={true} className={classes.activeProcedureRow}>
          <Text className={classes.activeProcedureLabel}>{`Active ${procedureString}:`}</Text>
          <ProcedureChip
            size={ChipSize.MINI}
            id={props.procedureId}
            flowchartVersionId={props.flowchartVersionId}
          />
        </Group>
      </div>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  sidepanelContent: {
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
  },
  infoBannerWrapper: {
    marginTop: theme.spacing.md,
  },
  planSettingsDivider: {
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.md,
  },
  runSectionWrapper: {
    marginTop: theme.spacing.md,
    borderTop: `1px solid ${Color.GRAY_200}`,
    paddingTop: theme.spacing.md,
  },
  showArchivedWrapper: {
    marginTop: theme.spacing.md,
  },
  showArchivedLabel: {
    fontWeight: 400,
    fontSize: theme.fontSizes.sm,
    lineHeight: theme.fontSizes.lg,
  },
  activeProcedureRow: {
    marginTop: 0,
  },
  activeProcedureLabel: {
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '14px',
  },
  splitPoolBannerText: {
    fontWeight: 400,
  },
  splitPoolActiveMethodName: {
    fontWeight: 600,
  },
}));
